import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import {
    BodyTextMedium,
    HeadingText30,
    HeadingText50 as StepTitle,
    Input,
    Checkbox,
    InputType,
    HeadingText10,
    DeprecatedInlineMessage,
    ButtonGroup,
    Button,
    ButtonGroupTestID,
    pxToRem,
} from '@modernhealth/vital-components';

import styled from '@emotion/styled';

import { mediaQueries } from 'src/shared/styles/variables';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { isPhoneNumberFormatValid } from 'src/shared/helpers/phoneValidator';

import { CareNavigationRequest } from '../models/benefits';
import { useAddCareNavigationRequestMutation } from '../documents/addCareNavigationRequestMutation.generated';
import { config } from 'src/config/config';
import {
    CareNavigationRequestContactPreference,
    CareNavigationRequestSource,
    CareNavigationRequestTopic,
    DeviceCategory,
} from 'src/generated/webapp_gql';
import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { Dispatch } from 'src/config/rootStore';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { CareNavigationContactDetailsStepTestId } from '../models/testsIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(32),
    marginBottom: pxToRem(24),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
    maxWidth: pxToRem(400),
});

const FormContainer = styled.div({
    textAlign: 'center',
    width: '100%',
    [mediaQueries.sm]: {
        paddingBottom: 0,
    },
});

const ContactPreferenceContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const InsideFormContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
});

const CheckboxContainer = styled.div({
    marginBottom: pxToRem(24),
    marginTop: pxToRem(16),
    width: '100%',
});

const EditLinkContainer = styled.div({
    cursor: 'pointer',
});

const CareNavigationInlineMessage = styled.div({
    marginTop: pxToRem(20),
});

export const CareNavigationContactDetailsStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest>();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();

    if (!isUserEnabledFlow) {
        navigationService.navigate('/dashboard');
    }

    if (isEmpty(location.state)) {
        navigationService.navigate('/care-navigation-request');
    }

    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const [requestState, setRequestState] = useState<CareNavigationRequest>(location.state);
    const [hasValidPhoneNumber, setHasValidPhoneNumber] = useState(
        isPhoneNumberFormatValid(user.phoneNumber),
    );
    const isPhone = requestState.contactPreference === CareNavigationRequestContactPreference.Phone;
    const [isEditing, setIsEditing] = useState(!hasValidPhoneNumber && isPhone);
    const [isSaveSettingsChecked, setIsSaveSettingsChecked] = useState(true);
    const [formTitle, setFormTitle] = useState(
        isEditing
            ? 'benefits:care_navigation_contact_information_step.update_contact_preference'
            : 'benefits:care_navigation_contact_information_step.lets_make_sure',
    );

    let contactPreferenceLowerCase: string, inputText: string, secondButtonText: string;

    if (location.state.contactPreference === CareNavigationRequestContactPreference.Phone) {
        contactPreferenceLowerCase = t(
            'benefits:care_navigation_contact_information_step.phone_lower_case',
        );
        inputText = t('benefits:care_navigation_contact_information_step.phone');
        secondButtonText = t('benefits:care_navigation_contact_information_step.request_call');
    } else {
        contactPreferenceLowerCase = t(
            'benefits:care_navigation_contact_information_step.email_lower_case',
        );
        inputText = t('benefits:care_navigation_contact_information_step.email');
        secondButtonText = t('benefits:care_navigation_contact_information_step.request_email');
    }

    useEffect(() => {
        setRequestState({
            ...requestState,
            contactPhone: user.phoneNumber,
            contactEmail: user.email,
        });
    }, [requestState.contactPreference, user.phoneNumber, user.email]);

    const dispatch: Dispatch = useDispatch();

    const handleRequest = () => {
        addCareNavigationRequestMutation({
            variables: {
                contactEmail:
                    requestState.contactPreference === CareNavigationRequestContactPreference.Email
                        ? requestState.contactEmail
                        : '',
                contactPhone:
                    requestState.contactPreference === CareNavigationRequestContactPreference.Phone
                        ? requestState.contactPhone
                        : '',
                contactPreference:
                    requestState.contactPreference as CareNavigationRequestContactPreference,
                message: requestState.message,
                source: requestState.source as CareNavigationRequestSource,
                topic: requestState.topic as CareNavigationRequestTopic,
                platform: requestState.platform as DeviceCategory,
                updatePhone:
                    requestState.contactPreference === CareNavigationRequestContactPreference.Phone
                        ? isSaveSettingsChecked
                        : false,
            },
        })
            .then(async response => {
                if (response?.data?.addCareNavigationRequest?.ok) {
                    navigationService.navigate('/care-navigation-all-set', requestState);

                    if (isSaveSettingsChecked) {
                        await dispatch.user.fetchUserInfo();
                    }
                } else {
                    toastNotificationsService.renderErrorToast(config.messages.genericError);
                }
            })
            .catch(error => toastNotificationsService.renderErrorToast(error));
    };

    const handleBack = () => {
        navigationService.navigate('/care-navigation-contact-method', requestState);
    };

    const handleEdit = () => {
        setFormTitle('benefits:care_navigation_contact_information_step.update_contact_preference');
        setIsEditing(true);
    };

    const toggleCheckbox = () => {
        setIsSaveSettingsChecked(!isSaveSettingsChecked);
    };

    const [addCareNavigationRequestMutation] = useAddCareNavigationRequestMutation();

    const definePhoneError = () => {
        if (!hasValidPhoneNumber) {
            return t('benefits:care_navigation_contact_information_step.phone.error');
        }
        return '';
    };

    return (
        <Wrapper>
            {requestState.source !== CareNavigationRequestSource.WellbeingReport && (
                <CareNavigationSteps step={4} />
            )}

            <TitleContainer>
                <StepTitle centered testID="contactPreferenceTitle">
                    {`${t(formTitle, {
                        contact_preference: t(contactPreferenceLowerCase),
                    })}`}
                </StepTitle>
            </TitleContainer>

            <FormContainer>
                {!isEditing && (
                    <>
                        <InsideFormContainer>
                            <ContactPreferenceContainer>
                                <HeadingText30 marginBottom={4}>{t(inputText)}</HeadingText30>
                                <BodyTextMedium testID="userContactPreference">
                                    {isPhone
                                        ? requestState.contactPhone
                                        : requestState.contactEmail}
                                </BodyTextMedium>
                            </ContactPreferenceContainer>

                            {isPhone && (
                                <>
                                    <EditLinkContainer onClick={handleEdit} data-testid="editLink">
                                        <HeadingText10 underline fontColor="blue55">
                                            {t('common.edit')}
                                        </HeadingText10>
                                    </EditLinkContainer>
                                </>
                            )}
                        </InsideFormContainer>
                        {!isPhone && (
                            <CareNavigationInlineMessage>
                                <DeprecatedInlineMessage
                                    action={{
                                        to: '/profile',
                                        text: `${t(
                                            'benefits:care_navigation_contact_information_step.email_banner_link',
                                        )}`,
                                    }}
                                    alignment="left"
                                    image=""
                                    message={t(
                                        'benefits:care_navigation_contact_information_step.email_banner',
                                    )}
                                />
                            </CareNavigationInlineMessage>
                        )}
                    </>
                )}

                {isEditing && isPhone && (
                    <>
                        <Input
                            label={t(inputText)}
                            onChange={value => {
                                setHasValidPhoneNumber(isPhoneNumberFormatValid(value.toString()));
                                setRequestState({
                                    ...requestState,
                                    contactPhone: value.toString(),
                                });
                            }}
                            onFocus={() => {}}
                            type={InputType.TextField}
                            value={requestState.contactPhone}
                            fieldId="contact-preference"
                            testID={CareNavigationContactDetailsStepTestId.ContactPreferenceInput}
                            error={definePhoneError()}
                        />
                        <CheckboxContainer>
                            <Checkbox
                                label={t(
                                    'benefits:care_navigation_contact_information_step.save_settings',
                                )}
                                onChange={toggleCheckbox}
                                testId={CareNavigationContactDetailsStepTestId.SaveSettingsCheckbox}
                                value={isSaveSettingsChecked}
                            />
                        </CheckboxContainer>
                    </>
                )}
                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ButtonGroupTestID.FirstButton}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleRequest}
                            disabled={isPhone && !hasValidPhoneNumber}
                            data-testid={ButtonGroupTestID.SecondButton}
                        >
                            {t(secondButtonText)}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>
            </FormContainer>
        </Wrapper>
    );
};
